import React, { ReactNode, type JSX } from 'react'
import Icon, { IconName } from '@dustin-web/microsite-components/src/icon'
import { LayoutResponsive, Site } from '@dustin-web/microsite-config'
import { NotificationBanner } from '@dustin-web/microsite-components/src/banners'
import HTMLReactParser from 'html-react-parser'

interface NavigationTile {
  icon: IconName
  href: string
  name: string
}

type Props = {
  translate: (key: string) => string
  site: Site
  responsive: LayoutResponsive
  children?: ReactNode
}

const HelpToFindContent = ({ site, translate, responsive, children }: Props): JSX.Element => {
  const assertUnreachable = (site: never): never => {
    throw new Error(`HelpToFindContent: unsupported ${site} site provided in switch case`)
  }

  const getPhoneNumber = (): string => {
    switch (site) {
      case 'DustinSweden':
        return translate('Dustin_SEB2B_Phone_Order')
      case 'DustinNorway':
        return translate('Dustin_NOB2B_Phone_Order')
      case 'DustinDenmark':
        return translate('Dustin_DKB2B_Phone_CustomerService')
      case 'DustinFinland':
        return translate('Dustin_FI_Phone_General')
      case 'DustinNetherlands':
        return translate('Dustin_NLB2B_Phone_General')
      case 'DustinBelgium':
        return translate('Dustin_BE_Phone_General')
      default:
        return assertUnreachable(site)
    }
  }

  const getEmail = (): string => {
    switch (site) {
      case 'DustinSweden':
        return translate('Dustin_SEB2B_Email_Order')
      case 'DustinNorway':
        return translate('Dustin_NOB2B_Email_Order')
      case 'DustinDenmark':
        return translate('Dustin_DKB2B_EmailCustomerService')
      case 'DustinFinland':
        return translate('Dustin_FIB2B_Email_CustomerService')
      case 'DustinNetherlands':
        return translate('Dustin_NLB2B_Email_CustomerService')
      case 'DustinBelgium':
        return translate('Dustin_BE_Email_CustomerService')
      default:
        return assertUnreachable(site)
    }
  }

  const email = getEmail()
  const phoneNumber = getPhoneNumber()
  const contactUsText = `${translate('HelpToFindContent_LinkToSupportText')}, ${translate(
    'HelpToFindContent_ContactUs'
  )} <a href="tel: ${phoneNumber}">${phoneNumber}</a>, ${translate(
    'HelpToFindContent_Or'
  )} <a href="mailto: ${email}">${email}</a>`

  const navigationTiles: NavigationTile[] = [
    {
      icon: 'idea',
      href: translate('New_Products_URL'),
      name: translate('Header_News'),
    },
    {
      icon: 'bargain',
      href: translate('Bargains_URL'),
      name: translate('Show_Bargains'),
    },
    {
      icon: 'mouse',
      href: '/accessories/guides',
      name: translate('Header_AccessoryFinder'),
    },
    {
      icon: 'star',
      href: translate('Brands_URL'),
      name: translate('BrandMenu_ShowAllBrands'),
    },
    {
      icon: 'service',
      href: translate('Header_Solutions_URL'),
      name: translate('Header_Solutions'),
    },
    {
      icon: 'user',
      href: '/account/overview',
      name: translate('MyAccount'),
    },
  ] as const

  return (
    <>
      {
        <div className="row sm-gutters u-mb-32">
          {navigationTiles.map((tile, key) => (
            <Tile responsive={responsive} tile={tile} key={key} />
          ))}
        </div>
      }
      {children && children}
      <NotificationBanner
        type="INFO"
        customIcon="headphones"
        title={translate('HelpToFindContent_NeedHelpHeader')}>
        {HTMLReactParser(contactUsText)}
      </NotificationBanner>
    </>
  )
}

const Tile = ({ tile, responsive }: { tile: NavigationTile; responsive: LayoutResponsive }) => {
  return responsive.breakpoints.min.MD ? (
    <div className="col-md-4 col-lg-2 u-mb-16">
      <a
        className="c-box u-border d-flex flex-column u-button-opacity u-h-100 u-decoration-none"
        style={{ minHeight: '9.2rem' }}
        href={tile.href}>
        <Icon name={tile.icon} className={`u-mb-8 i-20 i-${tile.icon}`} />
        <span className="u-fs-12">{tile.name}</span>
      </a>
    </div>
  ) : (
    <div className="col-12 col-sm-6">
      <a href={tile.href} className="a-stripped a-img d-block">
        <div className={'c-box d-flex align-items-center u-mb-8 u-pr-0 u-pr-8 u-py-12 u-pl-16'}>
          <Icon name={tile.icon} className={`i-20 i-${tile.icon}`} />
          <div className="flex-fill u-pl-8">
            <span className="d-block">{tile.name}</span>
          </div>
          <div className="d-flex align-items-center u-px-0">
            <Icon name="arrow-right-small" className="i-20 i-dark-gray" />
          </div>
        </div>
      </a>
    </div>
  )
}

export default HelpToFindContent
